import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  Switch,
  Image,
  Platform,
  KeyboardAvoidingView,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useSelector } from "react-redux";
import { useAppTheme } from "../../../../hooks/useAppTheme";
import { ThemeColors } from "../../../../utils/constants/colors";

// ─── Design Tokens (shared) ───────────────────────────────────────────────────

const getC = (colors: ThemeColors, isDark: boolean) => ({
  navy: isDark ? colors.surface : "#0F1B2D",
  navyMid: isDark ? colors.surfaceSubtle : "#1A2E46",
  navyLight: isDark ? colors.borderSubtle : "#243B55",
  accent: colors.primary,
  success: colors.success,
  successBg: isDark ? "rgba(14, 159, 110, 0.2)" : "#EDFAF4",
  warning: colors.warning,
  warningBg: isDark ? "rgba(245, 158, 11, 0.2)" : "#FFF8EC",
  danger: colors.danger,
  dangerBg: isDark ? "rgba(231, 76, 60, 0.2)" : "#FEF0EE",
  surface: colors.surface,
  surfaceAlt: isDark ? colors.surfaceSubtle : "#F7F9FC",
  border: colors.borderSubtle,
  borderLight: colors.borderSubtle,
  text: colors.text,
  textMid: colors.textSecondary,
  textMuted: colors.textMuted,
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContactRow {
  address: string;
  email: string;
  phoneNumber: string;
}
interface SalesRow {
  name: string;
  email: string;
}
interface OrgFormData {
  name: string;
  description: string;
  cardCode: string;
  prefix: string;
  logo: string;
  domains: string[];
  contacts: ContactRow[];
  sales: SalesRow[];
  states: string[];
  pricingEnabled: boolean;
  pricingModel: "default" | "sales";
  pricingSalesEmail: string | null;
  showAvailableStock: boolean;
  showOnOrderStock: boolean;
  showStockOnTransit: boolean;
  showGrowers: boolean;
}

interface Organization extends OrgFormData {
  _id?: string;
  logoUrl?: string;
}

interface AddEditOrganizationModalProps {
  visible: boolean;
  onClose: () => void;
  organization?: Organization | null;
  onSave: (data: OrgFormData) => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildDefault = (org?: Organization | null): OrgFormData => ({
  name: org?.name ?? "",
  description: org?.description ?? "",
  cardCode: org?.cardCode ?? "",
  prefix: org?.prefix ?? "",
  logo: org?.logo ?? "",
  domains: org?.domains ?? [],
  contacts: org?.contacts ?? [],
  sales: org?.sales ?? [],
  states: org?.states ?? [],
  pricingEnabled: org?.pricingEnabled ?? false,
  pricingModel: org?.pricingModel ?? "default",
  pricingSalesEmail: org?.pricingSalesEmail ?? null,
  showAvailableStock: org?.showAvailableStock ?? true,
  showOnOrderStock: org?.showOnOrderStock ?? true,
  showStockOnTransit: org?.showStockOnTransit ?? true,
  showGrowers: org?.showGrowers ?? true,
});

const validate = (form: OrgFormData): Record<string, string> => {
  const errs: Record<string, string> = {};
  if (!form.name.trim()) errs.name = "Organisation name is required";
  if (!form.cardCode.trim()) errs.cardCode = "Card code is required";
  if (!form.prefix.trim()) errs.prefix = "Order prefix is required";
  if (form.prefix.length > 5) errs.prefix = "Max 5 characters";
  if (form.states.length === 0) errs.states = "Select at least 1 state";
  if (form.pricingModel === "sales" && !form.pricingSalesEmail)
    errs.pricingSalesEmail = "Select a sales rep";
  form.contacts.forEach((c, i) => {
    if (!c.address.trim()) errs[`contact_${i}_address`] = "Required";
    if (!c.email.trim()) errs[`contact_${i}_email`] = "Required";
    if (!c.phoneNumber.trim()) errs[`contact_${i}_phoneNumber`] = "Required";
  });
  form.sales.forEach((s, i) => {
    if (!s.name.trim()) errs[`sales_${i}_name`] = "Required";
    if (!s.email.trim()) errs[`sales_${i}_email`] = "Required";
  });
  return errs;
};

// ─── Reusable sub-components ──────────────────────────────────────────────────

const SectionHeader = ({ icon, title }: { icon: string; title: string }) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const F = createF(C);
  return (
  <RNView style={F.sectionHeader}>
    <RNView style={F.sectionIconWrap}>
      <FeatherIcon name={icon as any} size={14} color={C.accent} />
    </RNView>
    <Text style={F.sectionTitle}>{title}</Text>
  </RNView>
);};

const FieldLabel = ({
  label,
  required,
}: {
  label: string;
  required?: boolean;
}) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const F = createF(C);
  return (
  <Text style={F.label}>
    {label}
    {required ? <Text style={{ color: C.danger }}> *</Text> : null}
  </Text>
);};

const FieldError = ({ msg }: { msg?: string }) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const F = createF(C);
  return msg ? <Text style={F.errorText}>{msg}</Text> : null;
};

const StyledInput = ({
  value,
  onChangeText,
  placeholder,
  multiline,
  lines,
  error,
  keyboardType,
  editable,
}: {
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  lines?: number;
  error?: string;
  keyboardType?: any;
  editable?: boolean;
}) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const F = createF(C);
  return (
  <RNView
    style={[
      F.inputWrap,
      !!error && F.inputWrapError,
      multiline && { height: (lines ?? 3) * 22 + 20 },
    ]}
  >
    <TextInput
      style={[
        F.input,
        multiline && { height: "100%", textAlignVertical: "top" },
      ]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={C.textMuted}
      multiline={multiline}
      numberOfLines={multiline ? lines : 1}
      keyboardType={keyboardType ?? "default"}
      editable={editable !== false}
    />
  </RNView>
);};

const ToggleRow = ({
  label,
  value,
  onToggle,
  description,
}: {
  label: string;
  value: boolean;
  onToggle: (v: boolean) => void;
  description?: string;
}) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const F = createF(C);
  return (
  <RNView style={F.toggleRow}>
    <RNView style={{ flex: 1 }}>
      <Text style={F.toggleLabel}>{label}</Text>
      {description && <Text style={F.toggleDesc}>{description}</Text>}
    </RNView>
    <Switch
      value={value}
      onValueChange={onToggle}
      trackColor={{ false: C.border, true: C.accent + "80" }}
      thumbColor={value ? C.accent : C.textMuted}
    />
  </RNView>
);};

// ─── States Picker ────────────────────────────────────────────────────────────

const StatesPicker = ({
  allStates,
  selected,
  onToggle,
}: {
  allStates: any[];
  selected: string[];
  onToggle: (id: string) => void;
}) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const F = createF(C);
  return (
  <RNView style={F.statesGrid}>
    {allStates.map((state: any) => {
      const isOn = selected.includes(state._id);
      return (
        <TouchableOpacity
          key={state._id}
          style={[F.stateChip, isOn && F.stateChipActive]}
          onPress={() => onToggle(state._id)}
          activeOpacity={0.7}
        >
          <Text style={[F.stateChipText, isOn && F.stateChipTextActive]}>
            {state.name}
          </Text>
          {isOn && <FeatherIcon name="check" size={11} color={C.accent} />}
        </TouchableOpacity>
      );
    })}
  </RNView>
);};

// ─── Sales Rep Picker (bottom sheet) ─────────────────────────────────────────

const SalesRepPicker = ({
  visible,
  sales,
  selected,
  onSelect,
  onClose,
}: {
  visible: boolean;
  sales: SalesRow[];
  selected: string | null;
  onSelect: (email: string | null) => void;
  onClose: () => void;
}) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const SRP = createSRP(C);
  return (
  <Modal
    visible={visible}
    transparent
    animationType="slide"
    onRequestClose={onClose}
  >
    <Pressable style={SRP.backdrop} onPress={onClose}>
      <Pressable style={SRP.sheet} onPress={(e) => e.stopPropagation()}>
        <RNView style={SRP.handle} />
        <Text style={SRP.title}>Select Sales Rep</Text>
        <TouchableOpacity
          style={SRP.option}
          onPress={() => {
            onSelect(null);
            onClose();
          }}
        >
          <Text style={[SRP.optionText, !selected && SRP.optionTextActive]}>
            None
          </Text>
          {!selected && <FeatherIcon name="check" size={14} color={C.accent} />}
        </TouchableOpacity>
        {sales.map((s) => (
          <TouchableOpacity
            key={s.email}
            style={SRP.option}
            onPress={() => {
              onSelect(s.email);
              onClose();
            }}
          >
            <RNView style={{ flex: 1 }}>
              <Text
                style={[
                  SRP.optionText,
                  selected === s.email && SRP.optionTextActive,
                ]}
              >
                {s.name}
              </Text>
              <Text style={SRP.optionSub}>{s.email}</Text>
            </RNView>
            {selected === s.email && (
              <FeatherIcon name="check" size={14} color={C.accent} />
            )}
          </TouchableOpacity>
        ))}
      </Pressable>
    </Pressable>
  </Modal>
);};

const createSRP = (C: any) => StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(10,20,35,0.45)",
    justifyContent: "flex-end",
  },
  sheet: {
    backgroundColor: C.surface,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    paddingTop: 12,
    maxHeight: "70%",
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.border,
    alignSelf: "center",
    marginBottom: 16,
  },
  title: { fontSize: 16, fontWeight: "800", color: C.text, marginBottom: 12 },
  option: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: C.borderLight,
  },
  optionText: { fontSize: 14, fontWeight: "600", color: C.textMid },
  optionTextActive: { color: C.accent },
  optionSub: { fontSize: 11, color: C.textMuted, marginTop: 2 },
});

// ─── Main Modal ───────────────────────────────────────────────────────────────

const AddEditOrganizationModal: React.FC<AddEditOrganizationModalProps> = ({
  visible,
  onClose,
  organization,
  onSave,
}) => {
  const { colors, isDark } = useAppTheme();
  const C = getC(colors, isDark);
  const SRP = createSRP(C);
  const F = createF(C);
  const M = createM(C);
  const allStates: any[] = useSelector(
    (state: any) => state.portal.admin?.states?.states ?? [],
  );

  const [form, setForm] = useState<OrgFormData>(buildDefault(organization));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [salesRepPickerVisible, setSalesRepPickerVisible] = useState(false);

  // Re-init when modal opens with a new org
  useEffect(() => {
    if (visible) {
      setForm(buildDefault(organization));
      setErrors({});
    }
  }, [visible, organization]);

  const set = useCallback(
    <K extends keyof OrgFormData>(key: K, value: OrgFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // ── Array helpers ─────────────────────────────────────────────────────────

  const addDomain = () => set("domains", [...form.domains, ""]);
  const removeDomain = (i: number) =>
    set(
      "domains",
      form.domains.filter((_, idx) => idx !== i),
    );
  const setDomain = (i: number, v: string) => {
    const arr = [...form.domains];
    arr[i] = v;
    set("domains", arr);
  };

  const addContact = () =>
    set("contacts", [
      ...form.contacts,
      { address: "", email: "", phoneNumber: "" },
    ]);
  const removeContact = (i: number) =>
    set(
      "contacts",
      form.contacts.filter((_, idx) => idx !== i),
    );
  const setContactField = (i: number, field: keyof ContactRow, v: string) => {
    const arr = form.contacts.map((c, idx) =>
      idx === i ? { ...c, [field]: v } : c,
    );
    set("contacts", arr);
  };

  const addSales = () => set("sales", [...form.sales, { name: "", email: "" }]);
  const removeSales = (i: number) =>
    set(
      "sales",
      form.sales.filter((_, idx) => idx !== i),
    );
  const setSalesField = (i: number, field: keyof SalesRow, v: string) => {
    const arr = form.sales.map((s, idx) =>
      idx === i ? { ...s, [field]: v } : s,
    );
    set("sales", arr);
  };

  const toggleState = (id: string) => {
    set(
      "states",
      form.states.includes(id)
        ? form.states.filter((s) => s !== id)
        : [...form.states, id],
    );
  };

  const handlePricingToggle = (checked: boolean) => {
    if (!checked) {
      set("pricingModel", "default");
      set("pricingSalesEmail", null);
    } else {
      set("pricingModel", "sales");
      if (form.sales.length === 1)
        set("pricingSalesEmail", form.sales[0].email);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSalesRep = form.sales.find(
    (s) => s.email === form.pricingSalesEmail,
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <RNView style={M.root}>
          {/* Header */}
          <RNView style={M.header}>
            <TouchableOpacity style={M.backBtn} onPress={onClose}>
              <FeatherIcon name="arrow-left" size={20} color="#fff" />
            </TouchableOpacity>
            <RNView style={{ flex: 1 }}>
              <Text style={M.headerTitle}>
                {organization ? "Edit Organisation" : "Create Organisation"}
              </Text>
            </RNView>
            <TouchableOpacity
              style={[M.saveBtn, submitting && { opacity: 0.6 }]}
              onPress={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <FeatherIcon name="save" size={14} color="#fff" />
                  <Text style={M.saveBtnText}>
                    {organization ? "Update" : "Create"}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </RNView>

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: 16, paddingBottom: 48 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* ── Logo placeholder ── */}
            <RNView style={M.logoSection}>
              <RNView style={M.logoCircle}>
                {organization?.logoUrl ? (
                  <Image
                    source={{ uri: organization.logoUrl }}
                    style={M.logoImg}
                  />
                ) : (
                  <FeatherIcon name="briefcase" size={28} color={C.accent} />
                )}
                <RNView style={M.logoCameraBtn}>
                  <FeatherIcon name="camera" size={12} color="#fff" />
                </RNView>
              </RNView>
              <Text style={M.logoHint}>Tap to upload logo</Text>
            </RNView>

            {/* ── Basic info ── */}
            <RNView style={M.card}>
              <SectionHeader icon="briefcase" title="Basic Information" />

              <FieldLabel label="Organisation Name" required />
              <StyledInput
                value={form.name}
                onChangeText={(v) => set("name", v)}
                placeholder="ACME Pty Ltd"
                error={errors.name}
              />
              <FieldError msg={errors.name} />

              <RNView style={{ height: 14 }} />
              <FieldLabel label="Description" />
              <StyledInput
                value={form.description}
                onChangeText={(v) => set("description", v)}
                placeholder="Brief description…"
                multiline
                lines={3}
              />

              <RNView style={{ height: 14 }} />
              <RNView style={M.twoCol}>
                <RNView style={{ flex: 1 }}>
                  <FieldLabel label="Card Code" required />
                  <StyledInput
                    value={form.cardCode}
                    onChangeText={(v) => set("cardCode", v)}
                    placeholder="C001"
                    error={errors.cardCode}
                  />
                  <FieldError msg={errors.cardCode} />
                </RNView>
                <RNView style={{ width: 12 }} />
                <RNView style={{ flex: 1 }}>
                  <FieldLabel label="Order Prefix" required />
                  <StyledInput
                    value={form.prefix}
                    onChangeText={(v) => set("prefix", v)}
                    placeholder="ACM"
                    error={errors.prefix}
                  />
                  <FieldError msg={errors.prefix} />
                </RNView>
              </RNView>
            </RNView>

            {/* ── Domains ── */}
            <RNView style={M.card}>
              <RNView style={M.cardHeaderRow}>
                <SectionHeader icon="globe" title="Domains" />
                <TouchableOpacity style={M.addBtn} onPress={addDomain}>
                  <FeatherIcon name="plus" size={13} color={C.accent} />
                  <Text style={M.addBtnText}>Add</Text>
                </TouchableOpacity>
              </RNView>
              {form.domains.length === 0 && (
                <Text style={M.emptyHint}>No domains added yet</Text>
              )}
              {form.domains.map((domain, i) => (
                <RNView key={i} style={M.arrayRow}>
                  <RNView style={{ flex: 1 }}>
                    <StyledInput
                      value={domain}
                      onChangeText={(v) => setDomain(i, v)}
                      placeholder="example.com"
                    />
                  </RNView>
                  <TouchableOpacity
                    style={M.removeBtn}
                    onPress={() => removeDomain(i)}
                  >
                    <FeatherIcon name="trash-2" size={14} color={C.danger} />
                  </TouchableOpacity>
                </RNView>
              ))}
            </RNView>

            {/* ── Contacts ── */}
            <RNView style={M.card}>
              <RNView style={M.cardHeaderRow}>
                <SectionHeader icon="phone" title="Contacts" />
                <TouchableOpacity style={M.addBtn} onPress={addContact}>
                  <FeatherIcon name="plus" size={13} color={C.accent} />
                  <Text style={M.addBtnText}>Add</Text>
                </TouchableOpacity>
              </RNView>
              {form.contacts.length === 0 && (
                <Text style={M.emptyHint}>No contacts added yet</Text>
              )}
              {form.contacts.map((contact, i) => (
                <RNView key={i} style={M.contactBlock}>
                  <RNView style={M.contactBlockHeader}>
                    <Text style={M.contactBlockTitle}>Contact {i + 1}</Text>
                    <TouchableOpacity onPress={() => removeContact(i)}>
                      <FeatherIcon name="trash-2" size={14} color={C.danger} />
                    </TouchableOpacity>
                  </RNView>
                  <FieldLabel label="Address" />
                  <StyledInput
                    value={contact.address}
                    onChangeText={(v) => setContactField(i, "address", v)}
                    placeholder="123 Main St, Sydney"
                    error={errors[`contact_${i}_address`]}
                  />
                  <FieldError msg={errors[`contact_${i}_address`]} />

                  <RNView style={{ height: 10 }} />
                  <RNView style={M.twoCol}>
                    <RNView style={{ flex: 1 }}>
                      <FieldLabel label="Email" />
                      <StyledInput
                        value={contact.email}
                        onChangeText={(v) => setContactField(i, "email", v)}
                        placeholder="hello@acme.com"
                        keyboardType="email-address"
                        error={errors[`contact_${i}_email`]}
                      />
                      <FieldError msg={errors[`contact_${i}_email`]} />
                    </RNView>
                    <RNView style={{ width: 10 }} />
                    <RNView style={{ flex: 1 }}>
                      <FieldLabel label="Phone" />
                      <StyledInput
                        value={contact.phoneNumber}
                        onChangeText={(v) =>
                          setContactField(i, "phoneNumber", v)
                        }
                        placeholder="+61 400 000 000"
                        keyboardType="phone-pad"
                        error={errors[`contact_${i}_phoneNumber`]}
                      />
                      <FieldError msg={errors[`contact_${i}_phoneNumber`]} />
                    </RNView>
                  </RNView>
                </RNView>
              ))}
            </RNView>

            {/* ── Sales Reps ── */}
            <RNView style={M.card}>
              <RNView style={M.cardHeaderRow}>
                <SectionHeader icon="users" title="Sales Representatives" />
                <TouchableOpacity style={M.addBtn} onPress={addSales}>
                  <FeatherIcon name="plus" size={13} color={C.accent} />
                  <Text style={M.addBtnText}>Add</Text>
                </TouchableOpacity>
              </RNView>
              {form.sales.length === 0 && (
                <Text style={M.emptyHint}>No sales reps added yet</Text>
              )}
              {form.sales.map((rep, i) => (
                <RNView key={i} style={M.contactBlock}>
                  <RNView style={M.contactBlockHeader}>
                    <Text style={M.contactBlockTitle}>Rep {i + 1}</Text>
                    <TouchableOpacity onPress={() => removeSales(i)}>
                      <FeatherIcon name="trash-2" size={14} color={C.danger} />
                    </TouchableOpacity>
                  </RNView>
                  <RNView style={M.twoCol}>
                    <RNView style={{ flex: 1 }}>
                      <FieldLabel label="Name" />
                      <StyledInput
                        value={rep.name}
                        onChangeText={(v) => setSalesField(i, "name", v)}
                        placeholder="Jane Smith"
                        error={errors[`sales_${i}_name`]}
                      />
                      <FieldError msg={errors[`sales_${i}_name`]} />
                    </RNView>
                    <RNView style={{ width: 10 }} />
                    <RNView style={{ flex: 1 }}>
                      <FieldLabel label="Email" />
                      <StyledInput
                        value={rep.email}
                        onChangeText={(v) => setSalesField(i, "email", v)}
                        placeholder="jane@acme.com"
                        keyboardType="email-address"
                        error={errors[`sales_${i}_email`]}
                      />
                      <FieldError msg={errors[`sales_${i}_email`]} />
                    </RNView>
                  </RNView>
                </RNView>
              ))}
            </RNView>

            {/* ── Pricing Model ── */}
            <RNView style={M.card}>
              <SectionHeader icon="dollar-sign" title="Pricing Model" />
              <Text style={M.sectionDesc}>
                Choose whether this organisation sees default prices or a
                specific sales rep's prices.
              </Text>
              <ToggleRow
                label="Use sales rep pricing"
                description="Instead of default pricing"
                value={form.pricingModel === "sales"}
                onToggle={handlePricingToggle}
              />
              {form.pricingModel === "sales" &&
                (form.sales.length === 0 ? (
                  <RNView style={M.warnBanner}>
                    <FeatherIcon
                      name="alert-triangle"
                      size={14}
                      color={C.warning}
                    />
                    <Text style={M.warnText}>
                      No sales reps yet. Add reps above first.
                    </Text>
                  </RNView>
                ) : (
                  <>
                    <FieldLabel label="Sales Representative" required />
                    <TouchableOpacity
                      style={M.pickerBtn}
                      onPress={() => setSalesRepPickerVisible(true)}
                    >
                      <RNView style={{ flex: 1 }}>
                        {selectedSalesRep ? (
                          <>
                            <Text style={M.pickerBtnValue}>
                              {selectedSalesRep.name}
                            </Text>
                            <Text style={M.pickerBtnSub}>
                              {selectedSalesRep.email}
                            </Text>
                          </>
                        ) : (
                          <Text style={M.pickerBtnPlaceholder}>
                            Select a sales rep…
                          </Text>
                        )}
                      </RNView>
                      <FeatherIcon
                        name="chevron-down"
                        size={15}
                        color={C.textMuted}
                      />
                    </TouchableOpacity>
                    <FieldError msg={errors.pricingSalesEmail} />
                  </>
                ))}

              {/* Summary */}
              <RNView style={M.pricingSummary}>
                <Text style={M.pricingSummaryText}>
                  Current model:{" "}
                  <Text style={{ fontWeight: "800", color: C.text }}>
                    {form.pricingModel === "sales"
                      ? selectedSalesRep
                        ? `Sales — ${selectedSalesRep.name}`
                        : "Sales — none selected"
                      : "Default pricing"}
                  </Text>
                </Text>
              </RNView>
            </RNView>

            {/* ── States ── */}
            <RNView style={M.card}>
              <SectionHeader icon="map-pin" title="States" />
              <FieldError msg={errors.states} />
              {allStates.length === 0 ? (
                <Text style={M.emptyHint}>Loading states…</Text>
              ) : (
                <StatesPicker
                  allStates={allStates}
                  selected={form.states}
                  onToggle={toggleState}
                />
              )}
            </RNView>

            {/* ── Visibility toggles ── */}
            <RNView style={M.card}>
              <SectionHeader icon="eye" title="Visibility Settings" />
              {(
                [
                  { key: "showAvailableStock", label: "Show Available Stock" },
                  { key: "showOnOrderStock", label: "Show On Order Stock" },
                  { key: "showStockOnTransit", label: "Show On Transit Stock" },
                  { key: "showGrowers", label: "Show Growers" },
                  { key: "pricingEnabled", label: "Allow Pricing Model" },
                ] as { key: keyof OrgFormData; label: string }[]
              ).map(({ key, label }) => (
                <ToggleRow
                  key={key}
                  label={label}
                  value={!!form[key]}
                  onToggle={(v) => set(key, v as any)}
                />
              ))}
            </RNView>
          </ScrollView>
        </RNView>
      </KeyboardAvoidingView>

      <SalesRepPicker
        visible={salesRepPickerVisible}
        sales={form.sales}
        selected={form.pricingSalesEmail}
        onSelect={(email) => set("pricingSalesEmail", email)}
        onClose={() => setSalesRepPickerVisible(false)}
      />
    </Modal>
  );
};

export default AddEditOrganizationModal;

// ─── Form Styles ──────────────────────────────────────────────────────────────

const createF = (C: any) => StyleSheet.create({
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 14,
  },
  sectionIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
  },
  sectionTitle: { fontSize: 14, fontWeight: "800", color: C.text },
  label: { fontSize: 12, fontWeight: "700", color: C.textMid, marginBottom: 5 },
  inputWrap: {
    borderWidth: 1.5,
    borderColor: C.border,
    borderRadius: 10,
    backgroundColor: C.surface,
    paddingHorizontal: 12,
    height: 44,
    justifyContent: "center",
  },
  inputWrapError: { borderColor: C.danger },
  input: { fontSize: 14, color: C.text },
  errorText: { fontSize: 11, color: C.danger, marginTop: 3 },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: C.borderLight,
  },
  toggleLabel: { fontSize: 14, fontWeight: "600", color: C.text },
  toggleDesc: { fontSize: 11, color: C.textMuted, marginTop: 1 },
  statesGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  stateChip: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: C.border,
    backgroundColor: C.surfaceAlt,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  stateChipActive: { borderColor: C.accent, backgroundColor: C.accent + "12" },
  stateChipText: { fontSize: 12, fontWeight: "600", color: C.textMid },
  stateChipTextActive: { color: C.accent, fontWeight: "700" },
});

const createM = (C: any) => StyleSheet.create({
  root: { flex: 1, backgroundColor: C.surfaceAlt },
  header: {
    backgroundColor: C.navyLight,
    paddingTop: Platform.OS === "ios" ? 52 : 16,
    paddingHorizontal: 16,
    paddingBottom: 14,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: C.navyLight,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: { fontSize: 18, fontWeight: "800", color: "#fff" },
  saveBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: C.accent,
    paddingVertical: 9,
    paddingHorizontal: 16,
    borderRadius: 20,
  },
  saveBtnText: { fontSize: 13, fontWeight: "800", color: "#fff" },

  logoSection: { alignItems: "center", paddingVertical: 20 },
  logoCircle: {
    width: 90,
    height: 90,
    borderRadius: 24,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  logoImg: { width: 90, height: 90, borderRadius: 24 },
  logoCameraBtn: {
    position: "absolute",
    bottom: -4,
    right: -4,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: C.accent,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: C.surface,
  },
  logoHint: { fontSize: 12, color: C.textMuted, marginTop: 8 },

  card: {
    backgroundColor: C.surface,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: C.borderLight,
    padding: 16,
    marginBottom: 12,
  },
  cardHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 0,
  },
  twoCol: { flexDirection: "row" },
  addBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: C.accent + "60",
    backgroundColor: C.accent + "10",
  },
  addBtnText: { fontSize: 12, fontWeight: "700", color: C.accent },
  removeBtn: {
    width: 38,
    height: 44,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 8,
  },
  arrayRow: { flexDirection: "row", alignItems: "center", marginTop: 10 },
  emptyHint: {
    fontSize: 12,
    color: C.textMuted,
    fontStyle: "italic",
    paddingVertical: 8,
  },
  contactBlock: {
    backgroundColor: C.surfaceAlt,
    borderRadius: 10,
    padding: 12,
    marginTop: 10,
  },
  contactBlockHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  contactBlockTitle: { fontSize: 13, fontWeight: "700", color: C.textMid },
  sectionDesc: {
    fontSize: 12,
    color: C.textMuted,
    marginBottom: 12,
    lineHeight: 18,
  },
  warnBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: C.warningBg,
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  warnText: { fontSize: 12, color: C.warning, flex: 1 },
  pickerBtn: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: C.border,
    borderRadius: 10,
    backgroundColor: C.surface,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 4,
  },
  pickerBtnValue: { fontSize: 14, fontWeight: "600", color: C.text },
  pickerBtnSub: { fontSize: 11, color: C.textMuted, marginTop: 2 },
  pickerBtnPlaceholder: { fontSize: 14, color: C.textMuted },
  pricingSummary: {
    marginTop: 12,
    backgroundColor: C.surfaceAlt,
    borderRadius: 8,
    padding: 12,
  },
  pricingSummaryText: { fontSize: 12, color: C.textMuted },
});
